import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../ui/Modal';
import CustomerLoginForm from './CustomerLoginForm';
import PointsPanel from './PointsPanel';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';

/**
 * The customer's controls in the booking-page header: a sign-in button when
 * signed out, and a points chip when signed in.
 *
 * ONLY RENDERS ON A LOCKED MEETING TYPE. An ordinary booking page has nothing
 * to sign in for, and putting a login button on it would suggest an account is
 * needed to book when it is not.
 *
 * Sits beside the host's share button rather than replacing it: a host looking
 * at their own page can see both.
 */

interface CustomerHeaderControlsProps {
  meetingTypeId?: number | null;
  organizationId?: number | null;
  /** The host, so the panel can link to their shops. */
  username?: string;
  /** True when this meeting type requires points and/or a plan. */
  locked: boolean;
}

const CustomerHeaderControls: React.FC<CustomerHeaderControlsProps> = ({
  organizationId,
  username,
  locked,
}) => {
  const { t } = useTranslation();
  const { isSignedIn, me, walletFor } = useCustomerAuth();

  const [loginOpen, setLoginOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);

  const wallet = walletFor(organizationId);

  // Only what they actually hold gets shown. A zero balance is not a fact worth
  // a chip, and "0 points" beside a live plan is actively misleading.
  const hasPoints = (wallet?.balance ?? 0) > 0;
  const planName =
    wallet?.subscription?.productTitle ??
    (wallet?.subscription ? t('booking:points.planFallback', 'Subscription') : null);

  if (!locked) return null;

  return (
    <>
      {isSignedIn ? (
        /**
         * WHO they are first, then what they hold.
         *
         * This was a bare "0 points" chip, which told someone with a plan and no
         * points that they had nothing — the one fact that was both prominent
         * and useless to them. The identity is always shown; points appear only
         * if there are any, the plan only if there is one, and both when both.
         * With neither, it is simply the account they are signed in to.
         */
        <button
          type="button"
          onClick={() => setPanelOpen(true)}
          /**
           * Padding is symmetric when the avatar is alone — and `justify-center`
           * is what actually centres it.
           *
           * Symmetric padding was not enough on its own: `accessibility.css`
           * gives every button a 44px minimum touch target, so an avatar-only
           * chip is 44px wide around a 28px circle. Flex's default alignment
           * pins that circle to the left and hands the whole 6px of slack to
           * the right, which is the visible off-centre look. With text present
           * the content is wider than 44px, so this changes nothing there.
           */
          className={`inline-flex items-center justify-center gap-2 rounded-full border border-secondary-300 bg-white py-1 text-sm font-medium text-secondary-800 hover:border-primary-400 hover:text-primary-700 ${
            hasPoints || planName ? 'pl-1 pr-3' : 'px-1'
          }`}
          title={t('booking:points.openPanel', 'Points and plan')}
        >
          {/* The initial of the signed-in address — an avatar without needing
              one to have been uploaded. */}
          <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-semibold uppercase text-primary-700">
            {(me?.email || '?').charAt(0)}
          </span>

          {hasPoints && (
            <span>
              {t('booking:points.balance', '{{count}} points', { count: wallet!.balance })}
            </span>
          )}

          {/* A dot only when both sit side by side, so neither runs into the
              other and a single value needs no separator. */}
          {hasPoints && planName && <span className="text-secondary-300">·</span>}

          {planName && <span className="max-w-[140px] truncate">{planName}</span>}
        </button>
      ) : (
        /* The SailLab button, with no icon. A person icon beside the word
           "Sign in" says nothing the word does not, and the outlined grey
           button read as a disabled control next to the host's own actions. */
        <button
          type="button"
          onClick={() => setLoginOpen(true)}
          className="btn-primary btn-sm"
        >
          {t('booking:customer.signIn', 'Sign in')}
        </button>
      )}

      <Modal
        isOpen={loginOpen}
        onClose={() => setLoginOpen(false)}
        title={t('booking:customer.signInTitle', 'Sign in')}
        size="small"
      >
        <CustomerLoginForm
          onSignedIn={() => setLoginOpen(false)}
          onCancel={() => setLoginOpen(false)}
        />
      </Modal>

      {/* Buying happens in the SHOP now, not in a payment form inside the
          panel — that one could not complete a purchase, and it offered plans
          the viewer was already on. The panel links out instead. */}
      <PointsPanel
        isOpen={panelOpen}
        onClose={() => setPanelOpen(false)}
        wallet={wallet}
        username={username}
      />
    </>
  );
};

export default CustomerHeaderControls;
